import React from 'react';
import {
  usePlatformStatus,
  useAdminStatus,
  platformState,
  hasAdminSession,
  useStateAnnouncement,
  timeAgo,
  STATE_TEXT,
} from '@/hooks/useSystemStatus';

/**
 * Bottom status strip: the one place the chrome reports platform health.
 * Every value comes from the backend (/api/system/status,
 * /api/admin/system/status); nothing is decorative. Platform-wide counts
 * are labelled as such so they are not mistaken for the tenant's own
 * Inbox numbers. A failed poll keeps the last good values and marks them
 * stale; an unreachable API is reported as that, not as "feeds down".
 */
function Item({ label, value, title, optional }) {
  return (
    <span className={`is-statusbar-item${optional ? ' is-statusbar-optional' : ''}`} title={title}>
      <span>{label}</span>
      <strong>{value}</strong>
    </span>
  );
}

const Sep = ({ optional }) => (
  <span className={`is-statusbar-sep${optional ? ' is-statusbar-optional' : ''}`} aria-hidden="true" />
);

const fmt = (n) => (typeof n === 'number' ? n.toLocaleString() : '-');

export default function StatusStrip({ variant = 'customer', marking = false }) {
  // Admin counters need an admin session; before sign-in (the admin login
  // page) the strip shows the public platform values instead of empty slots.
  const adminConsole = variant === 'admin';
  const isAdmin = adminConsole && hasAdminSession();
  const platform = usePlatformStatus();
  const admin = useAdminStatus(isAdmin);
  const p = platform.data;
  const { state, stale } = platformState(platform);
  const [stateLabel, stateHelp] = STATE_TEXT[state] || [state, ''];
  const announcement = useStateAnnouncement(state);
  const adminUnavailable = isAdmin && admin.loaded && !admin.data;

  const items = isAdmin
    ? [
        { label: 'Companies', value: fmt(admin.data?.companies) },
        { label: 'Pending IOCs', value: fmt(admin.data?.pending_auto_iocs), title: 'Auto-extracted IOCs awaiting review' },
        { label: 'Pending campaigns', value: fmt(admin.data?.pending_campaigns) },
        { label: 'Analysis queue', value: fmt(admin.data?.unprocessed_articles), title: 'Scraped articles not yet analyzed', optional: true },
      ]
    : [
        {
          label: 'Sources',
          value: p ? `${p.sources_up} / ${p.sources_total}` : '-',
          title: 'Feeds that returned content in the last 6 hours',
        },
        { label: 'Platform stories', value: fmt(p?.stories), title: 'Correlated stories across the whole platform (not only your Inbox)' },
        { label: 'Platform CVEs', value: fmt(p?.cves_tracked), title: 'Distinct CVEs referenced across the platform', optional: true },
      ];

  const lastIngest = isAdmin ? admin.data?.last_ingest_at : p?.last_ingest_at;

  return (
    <div className="is-statusbar" data-testid="status-strip" role="region" aria-label="Platform status">
      {/* The app shell has no classification bar, so the strip carries the marking there. */}
      {marking && (
        <span
          className="is-class-tag"
          title={adminConsole ? 'Administrator console' : 'Traffic Light Protocol: content is from public sources and may be shared freely'}
        >
          {adminConsole ? 'Admin' : 'TLP:CLEAR'}
        </span>
      )}
      <span className="is-statusbar-item" title={stateHelp}>
        <span className={`is-statusbar-dot is-dot--${state}`} aria-hidden="true" />
        <strong>{stateLabel}</strong>
        {stale && <span className="is-statusbar-stale">· stale, retrying</span>}
        <span className="sr-only" aria-live="polite">{announcement}</span>
      </span>
      {items.map((it) => (
        <React.Fragment key={it.label}>
          <Sep optional={it.optional} />
          <Item {...it} title={adminUnavailable ? 'Admin status unavailable' : it.title} />
        </React.Fragment>
      ))}
      <span style={{ marginLeft: 'auto' }} />
      <Item label="Last ingest" value={timeAgo(lastIngest)} title="When the most recent article was collected" />
      {p?.version && (
        <>
          <Sep optional />
          <Item label="Version" value={p.version} optional />
        </>
      )}
    </div>
  );
}
